import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useWasapiModules } from 'dashboard/wasapi/modules/useWasapiModules';

/**
 * Sidebar entries contributed by Wasapi modules. Keeps the core Sidebar.vue edit to a single
 * spread: `...wasapiSidebarItems.value`.
 */
export function useWasapiSidebarItems(accountScopedRoute) {
  const { t } = useI18n();
  const store = useStore();
  const dashboardApps = useMapGetter('dashboardApps/getRecords');

  onMounted(() => {
    store.dispatch('dashboardApps/get');
  });

  const appItems = computed(() =>
    (dashboardApps.value || [])
      .filter(app => app.show_in_sidebar)
      .map(app => ({
        name: `WasapiApp-${app.id}`,
        label: app.title,
        icon: 'i-lucide-layout-grid',
        to: accountScopedRoute('wasapi_dashboard_app', { appId: app.id }),
        activeOn: ['wasapi_dashboard_app'],
      }))
  );

  const { isAdmin } = useAdmin();
  const { isEnabled } = useWasapiModules();

  // Extension point for the Settings section. Empty today: Meta CAPI lives in each inbox's own
  // settings tab, and the module switches are an operator decision (Super Admin -> Account).
  const settingsItems = computed(() => []);

  const items = computed(() =>
    [
      {
        name: 'WasapiCRM',
        module: 'crm',
        label: t('WASAPI_CRM.SIDEBAR_LABEL'),
        icon: 'i-lucide-kanban',
        to: accountScopedRoute('wasapi_crm_board'),
        activeOn: ['wasapi_crm_board'],
      },
      {
        name: 'WasapiInternalChats',
        module: 'internal_chat',
        label: t('WASAPI_INTERNAL_CHATS.SIDEBAR_LABEL'),
        icon: 'i-lucide-messages-square',
        to: accountScopedRoute('wasapi_internal_chats'),
        activeOn: ['wasapi_internal_chats'],
      },
      {
        name: 'WasapiAppointments',
        module: 'appointments',
        label: t('WASAPI_APPOINTMENTS.SIDEBAR_LABEL'),
        icon: 'i-lucide-calendar-check',
        to: accountScopedRoute('wasapi_appointments'),
        activeOn: ['wasapi_appointments'],
      },
      {
        name: 'WasapiScheduler',
        module: 'scheduler',
        label: t('WASAPI_SCHEDULER.SIDEBAR_LABEL'),
        icon: 'i-lucide-calendar-clock',
        to: accountScopedRoute('wasapi_scheduler_index'),
        activeOn: ['wasapi_scheduler_index'],
      },
      {
        name: 'WasapiProjects',
        module: 'projects',
        label: t('WASAPI_PROJECTS.SIDEBAR_LABEL'),
        icon: 'i-lucide-square-kanban',
        to: accountScopedRoute('wasapi_projects'),
        activeOn: ['wasapi_projects'],
      },
      ...(isAdmin.value
        ? [
            {
              name: 'WasapiFlows',
              module: 'flows',
              label: t('WASAPI_FLOWS.SIDEBAR_LABEL'),
              icon: 'i-lucide-workflow',
              to: accountScopedRoute('wasapi_flows_index'),
              activeOn: ['wasapi_flows_index', 'wasapi_flow_edit'],
            },
          ]
        : []),
      ...appItems.value,
    ].filter(item => !item.module || isEnabled(item.module))
  );

  return { wasapiSidebarItems: items, wasapiSettingsItems: settingsItems };
}
